import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApis } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { FaSearch, FaArrowLeft, FaShoppingCart, FaPlus, FaMinus } from 'react-icons/fa';
import InchInput from '../components/InchInput';
import { filterProductList, filterSubcategoryList, pruneProductTree } from '../utils/visibility';

const AddProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productApis } = useApis();
  const { user } = useAuth();
  
  // Get data passed from CreateEstimate
  const { estimate, customerId, editMode, productToEdit } = location.state || {};
  
  // State management
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [inputValues, setInputValues] = useState({});
  const [selectedVariations, setSelectedVariations] = useState({});
  const [selectedVariationDetails, setSelectedVariationDetails] = useState({});
  const [showVariationDetailsModal, setShowVariationDetailsModal] = useState(false);
  const [currentVariationDetails, setCurrentVariationDetails] = useState([]);
  const [currentVariationId, setCurrentVariationId] = useState('');
  const [price, setPrice] = useState(0);
  const [calculating, setCalculating] = useState(false);
  
  // Subcategory selection state (matching mobile app)
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  
  // Cache notification state
  const [showCacheNotification, setShowCacheNotification] = useState(false);
  const [cacheTimeRemaining, setCacheTimeRemaining] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);

  // Cache key for localStorage
  const cacheKey = `addProduct_cache_${customerId}_${estimate?.cartID || estimate?.orderID}`;
  const CACHE_DURATION = 60 * 1000; // 1 minute in milliseconds

  // Function to save current form state to cache
  const saveToCache = useCallback(() => {
    if (!selectedProduct || editMode) return; // Don't cache in edit mode
    
    const cacheData = {
      timestamp: Date.now(),
      selectedProduct,
      productDetails,
      quantity,
      inputValues,
      selectedVariations,
      selectedVariationDetails,
      price,
      selectedCategory,
      searchTerm
    };
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log('💾 AddProduct: Form data cached');
    } catch (error) {
      console.error('❌ AddProduct: Error saving to cache:', error);
    }
  }, [selectedProduct, editMode, productDetails, quantity, inputValues, selectedVariations, selectedVariationDetails, price, selectedCategory, searchTerm, cacheKey]);

  // Function to load cached data if within time limit
  const loadFromCache = useCallback(() => {
    if (editMode) return false; // Don't load cache in edit mode
    
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (!cachedData) return false;
      
      const parsed = JSON.parse(cachedData);
      const timeDiff = Date.now() - parsed.timestamp;
      
      // Check if cache is still valid (within 1 minute)
      if (timeDiff > CACHE_DURATION) {
        localStorage.removeItem(cacheKey);
        console.log('⏰ AddProduct: Cache expired, removed');
        return false;
      }
      
      // Restore cached data
      if (parsed.selectedProduct) {
        setSelectedProduct(parsed.selectedProduct);
        console.log('🔄 Cache: Restored selectedProduct:', parsed.selectedProduct);
      }
      if (parsed.productDetails) {
        setProductDetails(parsed.productDetails);
        console.log('🔄 Cache: Restored productDetails:', parsed.productDetails);
      }
      if (parsed.quantity) setQuantity(parsed.quantity);
      if (parsed.inputValues) setInputValues(parsed.inputValues);
      if (parsed.selectedVariations) setSelectedVariations(parsed.selectedVariations);
      if (parsed.selectedVariationDetails) setSelectedVariationDetails(parsed.selectedVariationDetails);
      if (parsed.price) setPrice(parsed.price);
      if (parsed.selectedCategory) {
        setSelectedCategory(parsed.selectedCategory);
        console.log('🔄 Cache: Restored selectedCategory:', parsed.selectedCategory);
      }
      if (parsed.searchTerm) setSearchTerm(parsed.searchTerm);
      
      console.log('✅ AddProduct: Restored from cache', parsed);
      
      // Show cache notification and start countdown
      setShowCacheNotification(true);
      setShowCountdown(true);
      
      // Calculate remaining time
      const remainingTime = CACHE_DURATION - timeDiff;
      setCacheTimeRemaining(remainingTime);
      
      setTimeout(() => setShowCacheNotification(false), 3000); // Hide notification after 3 seconds
      
      return true;
    } catch (error) {
      console.error('❌ AddProduct: Error loading from cache:', error);
      localStorage.removeItem(cacheKey);
      return false;
    }
  }, [editMode, cacheKey, CACHE_DURATION]);

  // Function to clear cache
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(cacheKey);
      console.log('🗑️ AddProduct: Cache cleared');
    } catch (error) {
      console.error('❌ AddProduct: Error clearing cache:', error);
    }
  }, [cacheKey]);

  // Function to go back to category selection (clear current product)
  const handleBackToCategories = useCallback(() => {
    clearCache(); // Clear the cache
    setSelectedProduct(null);
    setProductDetails(null);
    setSelectedCategory(null);
    setQuantity(1);
    setInputValues({});
    setSelectedVariations({});
    setSelectedVariationDetails({});
    setPrice(0);
    setSearchTerm('');
    setShowCountdown(false);
    setCacheTimeRemaining(0);
    console.log('🔄 AddProduct: Reset to category selection');
  }, [clearCache]);

  // Define API functions first (before useEffect hooks that use them)
  const fetchSubcategories = useCallback(async () => {
    setLoadingSubcategories(true);
    try {
      const subcategories = await productApis.getProductSubcategories();
      setSubCategories(subcategories);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      alert('Error loading subcategories: ' + error.message);
    } finally {
      setLoadingSubcategories(false);
    }
  }, [productApis]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const storeProducts = await productApis.getStoreProducts({
        categoryId: parseInt(selectedCategory || '0'),
        searchTerm,
        take: 10000, // fetch all so visibility filtering can't leave a page blank
      });
      setProducts(storeProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Error loading products: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [productApis, selectedCategory, searchTerm]);

  const fetchProductDetails = useCallback(async (productId) => {
    try {
      console.log('🔄 AddProduct: Fetching product details for:', productId);
      const response = await fetch(`${API_URL}/products/${productId}/details?customerId=${customerId}`);
      const data = await response.json();

      if (data.success) {
        // Hide options this dealer isn't allowed to see (per-dealer visibility)
        data.product = pruneProductTree(data.product, user?.hidden_items);
        setProductDetails(data.product);
        console.log('✅ AddProduct: Product details fetched:', data.product);
      }
    } catch (error) {
      console.error('❌ AddProduct: Error fetching product details:', error);
    }
  }, [customerId, user]);

  // Load cached data on component mount (only if not in edit mode)
  useEffect(() => {
    if (!editMode) {
      const cacheLoaded = loadFromCache();
      if (cacheLoaded) {
        console.log('✅ AddProduct: Successfully loaded cached form data');
      }
    }
  }, [editMode, loadFromCache]);

  // Ensure product details are fetched if we have selectedProduct but no productDetails
  useEffect(() => {
    if (selectedProduct && !productDetails && !editMode && customerId) {
      console.log('🔄 AddProduct: Fetching missing product details for cached product:', selectedProduct.id);
      fetchProductDetails(selectedProduct.id);
    }
  }, [selectedProduct, productDetails, editMode, customerId, fetchProductDetails]);

  // Initialize form data if in edit mode
  useEffect(() => {
    if (editMode && productToEdit) {
      console.log('🔄 AddProduct: Initializing edit mode with product:', productToEdit);
      
      // Set basic product info
      setQuantity(productToEdit.quantity || 1);
      
      // Set selected product data directly
      if (productToEdit.product?.id) {
        const productData = {
          id: productToEdit.product.id,
          name: productToEdit.product.name,
          base_price: productToEdit.product.base_price
        };
        setSelectedProduct(productData);
        
        // Set product details from the existing product data
        // Since we're in edit mode, we have all the necessary data already
        setProductDetails({
          id: productToEdit.product.id,
          name: productToEdit.product.name,
          base_price: productToEdit.product.base_price || 0,
          variations: [], // Will be populated when product details are fetched
          groupedInputs: {} // Will be populated when product details are fetched
        });
      }
      
      // Set input values from existing data
      if (productToEdit.inputs) {
        const inputs = {};
        productToEdit.inputs.forEach(input => {
          inputs[input.inputId || input.inputName] = input.value;
        });
        setInputValues(inputs);
        console.log('🔄 AddProduct: Set input values:', inputs);
      }
      
      // Set variations from existing data
      if (productToEdit.variations) {
        const variations = {};
        const variationDetails = {};
        
        productToEdit.variations.forEach(variation => {
          variations[variation.variationId] = variation.variationItemId;
          
          if (variation.details) {
            variationDetails[variation.variationId] = {};
            variation.details.forEach(detail => {
              variationDetails[variation.variationId][detail.detailId] = detail.detailItemId;
            });
          }
        });
        
        setSelectedVariations(variations);
        setSelectedVariationDetails(variationDetails);
        console.log('🔄 AddProduct: Set variations:', variations);
        console.log('🔄 AddProduct: Set variation details:', variationDetails);
      }
      
      // Set price from existing data
      const existingPrice = parseFloat(
        productToEdit.customer_item_total || 
        productToEdit.item_total || 
        productToEdit.customerItemTotal ||
        productToEdit.itemTotal ||
        productToEdit.product_price ||
        productToEdit.customer_product_price ||
        0
      );
      setPrice(existingPrice);
      console.log('🔄 AddProduct: Set price from existing data:', existingPrice);
      
      // Fetch full product details after setting basic data
      if (productToEdit.product?.id && customerId) {
        fetchProductDetails(productToEdit.product.id);
        
        // If in edit mode, we should bypass category selection and go straight to product details
        // Set a dummy category to allow product loading
        setSelectedCategory('edit-mode');
      }
    }
  }, [editMode, productToEdit, customerId, fetchProductDetails]);

  // Fetch subcategories on component mount (matching mobile app)
  useEffect(() => {
    if (productApis) {
      fetchSubcategories();
    }
  }, [productApis, fetchSubcategories]);

  // Fetch products when selectedCategory or searchTerm changes (matching mobile app)
  useEffect(() => {
    if (productApis && selectedCategory) {
      fetchProducts();
    }
  }, [productApis, selectedCategory, searchTerm, fetchProducts]);

  // Save to cache when form data changes (debounced to avoid excessive saves)
  useEffect(() => {
    if (!editMode && selectedProduct) {
      const timeoutId = setTimeout(() => {
        saveToCache();
      }, 500); // Debounce for 500ms
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedProduct, productDetails, quantity, inputValues, selectedVariations, selectedVariationDetails, price, selectedCategory, searchTerm, editMode, saveToCache]);

  // Clear expired cache on component mount
  useEffect(() => {
    const clearExpiredCache = () => {
      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          const timeDiff = Date.now() - parsed.timestamp;
          if (timeDiff > CACHE_DURATION) {
            localStorage.removeItem(cacheKey);
            console.log('🧹 AddProduct: Cleared expired cache on mount');
          }
        }
      } catch (error) {
        console.error('❌ AddProduct: Error checking expired cache:', error);
      }
    };
    
    clearExpiredCache();
  }, [cacheKey, CACHE_DURATION]);

  // Cleanup: Save cache when component is about to unmount (but not when successfully adding product)
  useEffect(() => {
    return () => {
      // Only save cache if we have form data and not in edit mode
      if (!editMode && selectedProduct) {
        saveToCache();
        console.log('💾 AddProduct: Saved cache on component unmount');
      }
    };
  }, [editMode, selectedProduct, saveToCache]);

  // Countdown timer for cache expiration
  useEffect(() => {
    if (!showCountdown || cacheTimeRemaining <= 0) return;

    const interval = setInterval(() => {
      setCacheTimeRemaining(prev => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          setShowCountdown(false);
          clearCache();
          // Reset to category selection
          setSelectedProduct(null);
          setProductDetails(null);
          setSelectedCategory(null);
          console.log('⏰ Cache expired - resetting to category selection');
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showCountdown, cacheTimeRemaining, clearCache]);

  const handleProductSelect = async (product) => {
    // Clear any existing cache and countdown when selecting a new product
    clearCache();
    setShowCountdown(false);
    setCacheTimeRemaining(0);
    
    setSelectedProduct(product);
    
    try {
      // Fetch detailed product information
      const response = await fetch(`${API_URL}/products/${product.id}/details?customerId=${customerId}`);
      const data = await response.json();

      if (data.success) {
        // Hide options this dealer isn't allowed to see (per-dealer visibility)
        data.product = pruneProductTree(data.product, user?.hidden_items);
        setProductDetails(data.product);

        // Initialize input values
        const initialInputs = {};
        
        // Initialize product-level inputs only
        Object.values(data.product.groupedInputs || {}).forEach(group => {
          group.forEach(input => {
            initialInputs[input.id] = '';
          });
        });
        
        // Initialize variations (matching mobile app pattern)
        const initialVariations = {};
        const initialVariationDetails = {};
        data.product.variations?.forEach(variation => {
          const firstItem = variation.items?.[0];
          if (firstItem) {
            initialVariations[variation.id] = firstItem.id;
            
            // Initialize inputs for the first item if it has any
            if (firstItem.inputs && firstItem.inputs.length > 0) {
              firstItem.inputs.forEach(input => {
                initialInputs[input.id] = '';
              });
            }
            
            // Initialize variation details
            initialVariationDetails[variation.id] = {};
            firstItem.details?.forEach(detail => {
              const firstDetailItem = detail.detailItems?.[0];
              if (firstDetailItem) {
                initialVariationDetails[variation.id][detail.id] = firstDetailItem.id;
              }
            });
          }
        });
        setSelectedVariations(initialVariations);
        setSelectedVariationDetails(initialVariationDetails);
        
        // Set input values after initializing variation item inputs
        setInputValues(initialInputs);
        
        setPrice(data.product.base_price || 0);
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      alert('Error loading product details: ' + error.message);
    }
  };

  // Handle variation item selection (matching mobile app)
  const handleVariationItemSelect = (variationId, item) => {
    // Find the variation and get the previously selected item
    const variation = productDetails?.variations?.find(v => v.id === variationId);
    if (variation) {
      const previousItemId = selectedVariations[variationId];
      const previousItem = variation.items?.find(i => i.id === previousItemId);
      
      // Clear inputs from the previously selected item
      if (previousItem && previousItem.inputs && previousItem.inputs.length > 0) {
        setInputValues(prev => {
          const newInputValues = { ...prev };
          previousItem.inputs.forEach(input => {
            delete newInputValues[input.id];
          });
          return newInputValues;
        });
      }
      
      // Initialize inputs for the newly selected item
      if (item.inputs && item.inputs.length > 0) {
        setInputValues(prev => {
          const newInputValues = { ...prev };
          item.inputs.forEach(input => {
            newInputValues[input.id] = '';
          });
          return newInputValues;
        });
      }
    }
    
    setSelectedVariations(prev => ({
      ...prev,
      [variationId]: item.id
    }));
    
    // Reset detail selections for this variation
    const resetDetails = {};
    item.details?.forEach(detail => {
      const firstDetailItem = detail.detailItems?.[0];
      if (firstDetailItem) {
        resetDetails[detail.id] = firstDetailItem.id;
      }
    });
    
    setSelectedVariationDetails(prev => ({
      ...prev,
      [variationId]: resetDetails
    }));
    
    // Show details modal if there are details to configure
    if (item.details && item.details.length > 0) {
      setCurrentVariationDetails(item.details);
      setCurrentVariationId(variationId.toString());
      setShowVariationDetailsModal(true);
    }
  };

  // Handle showing variation details modal
  const handleShowVariationDetails = (variationId, details) => {
    setCurrentVariationDetails(details);
    setCurrentVariationId(variationId.toString());
    setShowVariationDetailsModal(true);
  };

  // Handle detail item selection in modal
  const handleDetailSelect = (detailId, detailItem) => {
    setSelectedVariationDetails(prev => ({
      ...prev,
      [currentVariationId]: {
        ...prev[currentVariationId],
        [detailId]: detailItem.id
      }
    }));
  };

  const calculatePrice = useCallback(async () => {
    if (!productDetails || !customerId || !user?.store_id) return;
    
    console.log('🔄 Calculating price with:', {
      customerId,
      storeId: user?.store_id,
      productId: productDetails.id,
      quantity
    });
    
    setCalculating(true);
    try {
      const response = await fetch(`${API_URL}/products/calculate-price?customerId=${customerId}&extensionId=4&storeId=${user?.store_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productDetails.id,
          inputs: Object.entries(inputValues).map(([id, value]) => ({
            inputId: Number(id),
            value
          })),
          variations: Object.entries(selectedVariations).map(([variationId, itemId]) => {
            const variation = productDetails.variations?.find(v => v.id === Number(variationId));
            const selectedItem = variation?.items?.find(item => item.id === Number(itemId));
            const details = selectedItem?.details?.map(detail => {
              const selectedDetailItemId = selectedVariationDetails[variationId]?.[detail.id];
              const selectedDetailItem = detail.detailItems?.find(item => item.id === Number(selectedDetailItemId));
              return {
                detailId: detail.id,
                detailItemId: selectedDetailItemId ? Number(selectedDetailItemId) : null,
                detailItemPrice: selectedDetailItem?.price || 0
              };
            }) || [];
            
            return {
              variationId: Number(variationId),
              variationItemId: Number(itemId),
              variationItemPrice: selectedItem?.price || 0,
              details
            };
          }),
          quantity
        })
      });
      
      if (!response.ok) {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`API responded with status ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        // Show the customer price (what the dealer pays to the store)
        const numericPrice = parseFloat(data.customerPrice ?? data.price) || 0;
        setPrice(numericPrice);
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    } finally {
      setCalculating(false);
    }
  }, [productDetails, customerId, user?.store_id, quantity, inputValues, selectedVariations, selectedVariationDetails]);

  // Recalculate price when inputs change
  useEffect(() => {
    if (productDetails) {
      const timeoutId = setTimeout(calculatePrice, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [inputValues, selectedVariations, selectedVariationDetails, quantity, productDetails, calculatePrice]);

  // Silent validation function (for button disabled state)
  const isFormValid = () => {
    if (!productDetails) return false;

    // Check required inputs
    if (productDetails.groupedInputs) {
      for (const group of Object.values(productDetails.groupedInputs)) {
        for (const input of group) {
          if (input.required && !inputValues[input.id]?.trim()) {
            return false;
          }
        }
      }
    }

    // Check required variations
    if (productDetails.variations) {
      for (const variation of productDetails.variations) {
        if (variation.required && !selectedVariations[variation.id]) {
          return false;
        }
      }
    }

    // Check required variation item inputs
    if (productDetails.variations) {
      for (const variation of productDetails.variations) {
        const selectedItemId = selectedVariations[variation.id];
        if (selectedItemId) {
          const selectedItem = variation.items?.find(item => item.id === selectedItemId);
          if (selectedItem?.inputs) {
            for (const input of selectedItem.inputs) {
              if (input.required && !inputValues[input.id]?.trim()) {
                return false;
              }
            }
          }
        }
      }
    }

    // Check quantity
    if (quantity <= 0) {
      return false;
    }

    return true;
  };

  // Form validation function with user feedback
  const validateForm = () => {
    if (!productDetails) {
      alert('Product details not loaded');
      return false;
    }

    // Check required inputs
    if (productDetails.groupedInputs) {
      for (const group of Object.values(productDetails.groupedInputs)) {
        for (const input of group) {
          if (input.required && !inputValues[input.id]?.trim()) {
            alert(`Please fill in the required field: ${input.name}`);
            return false;
          }
        }
      }
    }

    // Check required variations
    if (productDetails.variations) {
      for (const variation of productDetails.variations) {
        if (variation.required && !selectedVariations[variation.id]) {
          alert(`Please select a ${variation.title}`);
          return false;
        }
      }
    }

    // Check quantity
    if (quantity <= 0) {
      alert('Quantity must be greater than 0');
      return false;
    }

    return true;
  };

  const handleAddToCart = async () => {
    if (!productDetails || !estimate) {
      alert('Missing required information');
      return;
    }

    // Validate form before proceeding
    if (!validateForm()) {
      return;
    }

    try {
      const cartEndpoint = estimate.orderID ? 'order-carts' : 'carts';
      const cartId = estimate.orderID || estimate.cartID;
      
      let response;
      
      if (editMode && productToEdit?.id) {
        // Update existing cart item
        response = await fetch(`${API_URL}/customers/${customerId}/${cartEndpoint}/${cartId}/item/${productToEdit.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_id: productDetails.id,
            quantity,
            final_price: price,
            inputs: Object.entries(inputValues).map(([id, value]) => ({
              inputId: Number(id),
              value
            })),
            variations: Object.entries(selectedVariations).map(([variationId, itemId]) => {
              const variation = productDetails.variations?.find(v => v.id === Number(variationId));
              const selectedItem = variation?.items?.find(item => item.id === Number(itemId));
              const details = selectedItem?.details?.map(detail => {
                const selectedDetailItemId = selectedVariationDetails[variationId]?.[detail.id];
                const selectedDetailItem = detail.detailItems?.find(item => item.id === Number(selectedDetailItemId));
                return {
                  detailId: detail.id,
                  detailItemId: selectedDetailItemId ? Number(selectedDetailItemId) : null,
                  detailItemPrice: selectedDetailItem?.price || 0
                };
              }) || [];
              
              return {
                variationId: Number(variationId),
                variationItemId: Number(itemId),
                variationItemPrice: selectedItem?.price || 0,
                details
              };
            })
          })
        });
      } else {
        // Add new cart item
        response = await fetch(`${API_URL}/customers/${customerId}/${cartEndpoint}/${cartId}/item`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_id: productDetails.id,
            quantity,
            final_price: price,
            inputs: Object.entries(inputValues).map(([id, value]) => ({
              inputId: Number(id),
              value
            })),
            variations: Object.entries(selectedVariations).map(([variationId, itemId]) => {
              const variation = productDetails.variations?.find(v => v.id === Number(variationId));
              const selectedItem = variation?.items?.find(item => item.id === Number(itemId));
              const details = selectedItem?.details?.map(detail => {
                const selectedDetailItemId = selectedVariationDetails[variationId]?.[detail.id];
                const selectedDetailItem = detail.detailItems?.find(item => item.id === Number(selectedDetailItemId));
                return {
                  detailId: detail.id,
                  detailItemId: selectedDetailItemId ? Number(selectedDetailItemId) : null,
                  detailItemPrice: selectedDetailItem?.price || 0
                };
              }) || [];
              
              return {
                variationId: Number(variationId),
                variationItemId: Number(itemId),
                variationItemPrice: selectedItem?.price || 0,
                details
              };
            })
          })
        });
      }

      const data = await response.json();

      if (data.success) {
        // Clear cache on successful product addition
        clearCache();
        alert(editMode ? 'Product updated successfully!' : 'Product added to cart successfully!');
        navigate(-1); // Go back to CreateEstimate
      } else {
        throw new Error(data.message || 'Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product: ' + error.message);
    }
  };

  // Drop products this dealer isn't allowed to see, then apply the search filter
  const filteredProducts = filterProductList(products, user?.hidden_items).filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!selectedProduct && !editMode) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="px-6 py-4 flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              {selectedCategory 
                ? `Products - ${subCategories.find(cat => cat.id === selectedCategory)?.category_name || 'Category'}`
                : 'Select Category'
              }
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {selectedCategory ? (
            <>
              {/* Search - Only show when category is selected */}
              <div className="relative mb-6">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Back to Categories Button */}
              <div className="mb-6">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchTerm('');
                    setProducts([]);
                  }}
                  className="text-blue-600 hover:text-blue-800 flex items-center space-x-2 text-sm"
                >
                  <FaArrowLeft className="w-4 h-4" />
                  <span>Back to Categories</span>
                </button>
              </div>

              {/* Products Grid */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {searchTerm ? 'No products found matching your search.' : 'No products available in this category.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 cursor-pointer transition-all duration-200"
                    >
                      <h3 className="font-medium text-gray-900 mb-2">{product.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{product.product_category}</p>
                      <p className="text-lg font-semibold text-blue-600">${product.price}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Category Selection - Matching mobile app flow */}
              <div className="text-center mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-2">
                  Please select a category to view products
                </h2>
                <p className="text-sm text-gray-600">
                  Choose a category below to see available products
                </p>
              </div>

              {/* Subcategories Grid */}
              {loadingSubcategories ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : subCategories.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No categories available.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filterSubcategoryList(subCategories, user?.hidden_items).map((subcategory) => (
                    subcategory.storeProductCount > 0 && (
                      <div
                        key={subcategory.id}
                        onClick={() => {
                          setSelectedCategory(subcategory.id);
                          setSearchTerm('');
                        }}
                        className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 cursor-pointer transition-all duration-200"
                      >
                        <h3 className="font-medium text-gray-900">{subcategory.category_name}</h3>
                      </div>
                    )
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Cache Notification */}
      {showCacheNotification && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="text-sm">Previous selections restored</span>
        </div>
      )}

      {/* Cache Countdown Timer */}
      {showCountdown && cacheTimeRemaining > 0 && !editMode && (
        <div className="fixed top-4 left-4 bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="text-sm">
            Cache expires in: {Math.ceil(cacheTimeRemaining / 1000)}s
          </span>
        </div>
      )}

      {/* Variation Details Modal */}
      {showVariationDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Select Details</h2>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {currentVariationDetails.map((detail) => (
                <div key={detail.id} className="mb-6 last:mb-0">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">{detail.title}</h3>
                  <div className="space-y-2">
                    {detail.detailItems?.map((item) => {
                      const isSelected = selectedVariationDetails[currentVariationId]?.[detail.id] === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleDetailSelect(detail.id, item)}
                          className={`w-full p-3 border rounded-lg text-left transition-all duration-200 flex items-center justify-between ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <div>
                            <div className="font-medium">{item.name}</div>
                            {item.price > 0 && (
                              <div className="text-sm text-gray-600">+${item.price}</div>
                            )}
                          </div>
                          {isSelected && (
                            <div className="text-blue-500">✓</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowVariationDetailsModal(false)}
                className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {editMode ? 'Edit Product' : 'Add Product'}
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 p-2"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Product Configuration */}
      <div className="p-6 max-w-4xl mx-auto">
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {/* Product Info */}
          {selectedProduct && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 mb-1">{selectedProduct.name}</h2>
                  {showCountdown && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⏱️ Cached selections expire in {Math.ceil(cacheTimeRemaining / 1000)} seconds
                    </p>
                  )}
                </div>
                {!editMode && (
                  <button
                    onClick={handleBackToCategories}
                    className="ml-4 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    Change Product
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity *
            </label>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FaMinus className="w-4 h-4" />
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 text-center border border-gray-300 rounded-lg py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
              />
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FaPlus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Product Inputs */}
          {productDetails && Object.keys(productDetails.groupedInputs || {}).map((group) => (
            <div key={group} className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{group}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productDetails.groupedInputs[group].map((input) => (
                  <div key={input.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {input.name}
                      {input.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {/* Use InchInput for number inputs when measurement unit is INCH */}
                    {input.type === 'number' && input.measurement_unit === 'INCH' ? (
                      <InchInput
                        value={inputValues[input.id] || ''}
                        onChange={(newValue) => setInputValues(prev => ({
                          ...prev,
                          [input.id]: newValue
                        }))}
                        placeholder={`Enter ${input.name.toLowerCase()}${input.required ? ' (required)' : ''}`}
                        required={input.required}
                        className={`${
                          input.required && !inputValues[input.id]?.trim() 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                      />
                    ) : (
                      <input
                        type={input.type === 'number' ? 'number' : 'text'}
                        value={inputValues[input.id] || ''}
                        onChange={(e) => setInputValues(prev => ({
                          ...prev,
                          [input.id]: e.target.value
                        }))}
                        className={`w-full border rounded-lg py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          input.required && !inputValues[input.id]?.trim() 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                        placeholder={`Enter ${input.name.toLowerCase()}${input.required ? ' (required)' : ''}`}
                        required={input.required}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Variations */}
          {productDetails && productDetails.variations?.filter(variation => variation.required).map((variation) => (
            <div key={variation.id} className="mb-6">
              <label className={`block text-sm font-medium mb-3 ${
                variation.required && !selectedVariations[variation.id] 
                  ? 'text-red-700' 
                  : 'text-gray-700'
              }`}>
                {variation.title}
                {variation.required && <span className="text-red-500 ml-1">*</span>}
                {variation.required && !selectedVariations[variation.id] && (
                  <span className="text-red-500 text-xs ml-2">(Please select)</span>
                )}
              </label>
              
              {/* Variation Items */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {variation.items?.map((item) => {
                  const isSelected = selectedVariations[variation.id] === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleVariationItemSelect(variation.id, item)}
                      className={`p-3 border-2 rounded-lg text-left transition-all duration-200 ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">{item.name}</div>
                      {item.price > 0 && (
                        <div className="text-sm text-gray-600">+${item.price}</div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Variation Item Inputs */}
              {variation.items?.map((item) => {
                if (selectedVariations[variation.id] !== item.id) return null;
                if (!item.inputs || item.inputs.length === 0) return null;
                
                return (
                  <div key={`inputs-${item.id}`} className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      {item.name} Inputs
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {item.inputs.map((input) => (
                        <div key={input.id}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {input.name}
                            {input.measurement_unit && (
                              <span className="text-gray-500 ml-1">
                                ({input.measurement_unit.toLowerCase()})
                              </span>
                            )}
                            {input.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {/* Use InchInput for number inputs when measurement unit is INCH */}
                          {input.type === 'number' && input.measurement_unit === 'INCH' ? (
                            <InchInput
                              value={inputValues[input.id] || ''}
                              onChange={(newValue) => setInputValues(prev => ({
                                ...prev,
                                [input.id]: newValue
                              }))}
                              placeholder={`Enter ${input.name.toLowerCase()}${input.required ? ' (required)' : ''}`}
                              required={input.required}
                              className={`${
                                input.required && !inputValues[input.id]?.trim() 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                            />
                          ) : (
                            <input
                              type={input.type === 'number' ? 'number' : 'text'}
                              value={inputValues[input.id] || ''}
                              onChange={(e) => setInputValues(prev => ({
                                ...prev,
                                [input.id]: e.target.value
                              }))}
                              className={`w-full border rounded-lg py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                input.required && !inputValues[input.id]?.trim() 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                              placeholder={`Enter ${input.name.toLowerCase()}${input.required ? ' (required)' : ''}`}
                              required={input.required}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {/* Variation Details Summary */}
              {variation.items?.map((item) => {
                if (selectedVariations[variation.id] !== item.id) return null;
                if (!item.details || item.details.length === 0) return null;
                
                const summary = item.details.map(detail => {
                  const selectedDetailId = selectedVariationDetails[variation.id]?.[detail.id];
                  const selectedDetailItem = detail.detailItems?.find(di => di.id === Number(selectedDetailId));
                  return selectedDetailItem ? selectedDetailItem.name : `Select ${detail.title}`;
                }).join(', ');
                
                return (
                  <button
                    key={`details-${item.id}`}
                    type="button"
                    onClick={() => handleShowVariationDetails(variation.id, item.details)}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-left hover:bg-gray-100 transition-colors"
                  >
                    <div className="text-sm text-gray-600 mb-1">Details:</div>
                    <div className="text-gray-800">{summary}</div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Price and Add to Cart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Price</p>
              <p className="text-2xl font-bold text-gray-900">
                {calculating ? (
                  <span className="text-gray-400">Calculating...</span>
                ) : (
                  `$${(typeof price === 'number' ? price : 0).toFixed(2)}`
                )}
              </p>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={calculating || !productDetails || !isFormValid()}
              className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              title={!isFormValid() ? 'Please fill all required fields' : ''}
            >
              <FaShoppingCart className="w-4 h-4" />
              <span>{editMode ? 'Update Product' : 'Add to Cart'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AddProduct; 